import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/app/api/client"
import { normalizeStr, townshipRange } from "@/app/lib/str"
import type { BaseInvRow } from "@/app/lib/batchTransactions"

interface InventoryResponse {
  data: BaseInvRow[]
  meta: unknown
}
interface ReferenceResponse {
  references: { township_range: string; county: string }[]
  overrides: { str: string; county: string }[]
}

export interface StrOptions {
  ready: boolean
  inventoryByStr: Map<string, BaseInvRow>
  ownedStrs: string[]
  statewideStrs: string[]
  countyForStr: (str: string) => string
}

export function useStrOptions(enabled: boolean): StrOptions {
  const inventory = useQuery<InventoryResponse>({
    queryKey: ["inventories", "all"],
    queryFn: () => api.get<InventoryResponse>("/inventories?per_page=2000"),
    enabled,
  })
  const reference = useQuery<ReferenceResponse>({
    queryKey: ["township_county_references"],
    queryFn: () => api.get<ReferenceResponse>("/township_county_references"),
    enabled,
    staleTime: Infinity,
  })

  const inventoryByStr = useMemo(() => {
    const m = new Map<string, BaseInvRow>()
    for (const r of inventory.data?.data ?? []) {
      const key = normalizeStr(r.str)
      if (key) m.set(key, r)
    }
    return m
  }, [inventory.data])

  const ownedStrs = useMemo(() => [...inventoryByStr.keys()].sort(), [inventoryByStr])

  // Statewide candidate STRs: each TR reference x sections 1..36 (~3,240 short
  // strings, generated once). The combobox filters + caps rendered rows.
  const statewideStrs = useMemo(() => {
    const out: string[] = []
    for (const ref of reference.data?.references ?? []) {
      for (let sec = 1; sec <= 36; sec++) {
        const canonical = normalizeStr(`${sec}-${ref.township_range}`)
        if (canonical) out.push(canonical)
      }
    }
    return out
  }, [reference.data])

  // Global override map (overrides are shared reference data, not tenant-scoped).
  const overrideByStr = useMemo(() => {
    const m = new Map<string, string>()
    for (const o of reference.data?.overrides ?? []) {
      const key = normalizeStr(o.str)
      if (key) m.set(key, o.county)
    }
    return m
  }, [reference.data])

  const trCounty = useMemo(() => {
    const m = new Map<string, string>()
    for (const ref of reference.data?.references ?? []) m.set(ref.township_range, ref.county)
    return m
  }, [reference.data])

  const countyForStr = useMemo(() => {
    return (str: string): string => {
      const canonical = normalizeStr(str)
      if (!canonical) return ""
      // Resolver order (mirrors CountyResolver): override -> inventory row -> TR ref.
      if (overrideByStr.has(canonical)) return overrideByStr.get(canonical)!
      const inv = inventoryByStr.get(canonical)
      if (inv?.county) return inv.county
      const tr = townshipRange(canonical)
      return (tr && trCounty.get(tr)) || ""
    }
  }, [overrideByStr, inventoryByStr, trCounty])

  return {
    ready: inventory.isSuccess && reference.isSuccess,
    inventoryByStr,
    ownedStrs,
    statewideStrs,
    countyForStr,
  }
}
